import React, { forwardRef } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
} from "date-fns";
import { Droplet, Beef } from "lucide-react";
import { phaseForDate, computeIntake, cycleTimeline } from "../lib/cycle";
import { useI18n } from "../i18n";
import { METRIC_ICONS, directionMeta, focusMeta } from "./metricUtils";

const METRIC_KEY_MAP = { activity: "movement", carbs: "carbs", fat: "fats" };

function Dots({ level, color }) {
  return (
    <span className="inline-flex gap-1">
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className="h-1.5 w-4 rounded-full inline-block"
          style={{ backgroundColor: n <= level ? color : "#E5DFD3" }}
        />
      ))}
    </span>
  );
}

function MiniCalendar({ data, month, phases, weekdays, dateLocale }) {
  const gridStart = startOfWeek(startOfMonth(month));
  const gridEnd = endOfWeek(endOfMonth(month));
  const days = [];
  let cur = gridStart;
  while (cur <= gridEnd) {
    days.push(cur);
    cur = addDays(cur, 1);
  }
  const today = new Date();
  return (
    <div>
      <div className="text-center font-serif text-2xl mb-2">{format(month, "MMMM yyyy", { locale: dateLocale })}</div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdays.map((d, i) => (
          <div key={`${d}-${i}`} className="text-center text-[10px] font-bold text-[#8A857B]">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const inMonth = isSameMonth(day, month);
          const { phaseKey } = phaseForDate(day, data);
          const phase = phases[phaseKey];
          const isToday = isSameDay(day, today);
          return (
            <div
              key={day.toISOString()}
              className="aspect-square rounded-md flex items-center justify-center text-[11px] font-semibold"
              style={{
                backgroundColor: inMonth ? phase.color : "transparent",
                color: inMonth ? phase.textColor : "#C9C3B6",
                opacity: inMonth ? 1 : 0.3,
                outline: isToday ? "2px solid #2D2A26" : "none",
                outlineOffset: "1px",
              }}
            >
              {format(day, "d")}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ExportSheet = forwardRef(function ExportSheet({ data }, ref) {
  const { phases, t, dateLocale, weekdays } = useI18n();
  const today = new Date();
  const { phaseKey, cycleDay } = phaseForDate(today, data);
  const phase = phases[phaseKey];
  const intake = computeIntake(data.weight, phase.proteinFactor);
  const proteinMax = Math.round(Number(data.weight) * 1.15);
  const proteinBase = Math.round(Number(data.weight));
  const fMeta = focusMeta(phase.focus);

  const { days, start } = cycleTimeline(data);
  const end = new Date(start);
  end.setDate(start.getDate() + days.length - 1);

  const groups = [];
  days.forEach((d) => {
    const last = groups[groups.length - 1];
    if (last && last.key === d.phaseKey) last.days.push(d);
    else groups.push({ key: d.phaseKey, days: [d] });
  });

  const metrics = ["activity", "carbs", "fat"];

  return (
    <div
      ref={ref}
      style={{ width: 816, minHeight: 1056, backgroundColor: "#FDFBF7" }}
      className="text-[#2D2A26] p-12 font-sans"
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b border-[#E5DFD3] pb-5">
        <div>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-bold text-[#9B3926]">
            <span className="h-2 w-2 rounded-full bg-[#9B3926]" /> Cycle IQ
          </div>
          <h1 className="font-serif text-5xl leading-none mt-2">{t("exportHeadline")}</h1>
        </div>
        <div className="text-right">
          <div className="font-serif text-2xl">
            {format(start, "MMM d", { locale: dateLocale })} – {format(end, "MMM d", { locale: dateLocale })}
          </div>
          <div className="text-sm text-[#5C5954] mt-1">
            {t("cyclePeriodInfo", { c: data.cycleLength, p: data.periodLength })}
          </div>
        </div>
      </div>

      {/* Top: two columns */}
      <div className="grid grid-cols-2 gap-6 mt-6">
        {/* Left column */}
        <div className="space-y-4">
          <div className="rounded-2xl p-5" style={{ backgroundColor: phase.color, color: phase.textColor }}>
            <div className="text-[11px] tracking-[0.25em] uppercase font-bold opacity-80">
              {t("dayToday", { n: cycleDay })} · {t(phase.focus === "fitness" ? "fitnessWindow" : "hormoneCare")}
            </div>
            <div className="font-serif text-4xl leading-none mt-1.5">{phase.label}</div>
            <div className="text-sm font-semibold opacity-90 mt-0.5">{phase.short}</div>
            <p className="text-[13px] mt-3 leading-snug opacity-95">{phase.hormone}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-[#E5DFD3] p-3 flex items-center gap-2 bg-white">
              <Droplet className="h-5 w-5" style={{ color: "#3E7CB1" }} />
              <div>
                <div className="font-serif text-2xl leading-none">
                  {intake.waterOz} <span className="text-xs font-sans text-[#5C5954]">{t("flOz")}</span>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#8A857B]">{t("waterPerDay")}</div>
              </div>
            </div>
            <div className="rounded-xl border border-[#E5DFD3] p-3 flex items-center gap-2 bg-white">
              <Beef className="h-5 w-5" style={{ color: "#9B3926" }} />
              <div>
                <div className="font-serif text-2xl leading-none">
                  {intake.proteinG} <span className="text-xs font-sans text-[#5C5954]">{t("grams")}</span>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#8A857B]">{t("proteinPerDay")}</div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-[#E5DFD3] p-4 bg-white">
            <div className="text-[11px] tracking-[0.15em] uppercase font-bold text-[#8A857B] mb-2">
              {t("focusForPhase")}
            </div>
            <ul className="space-y-1.5">
              {phase.tips.map((tip) => (
                <li key={tip} className="flex gap-2 text-[13px] leading-snug">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: phase.color }} />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E5DFD3] p-4 bg-white">
          <MiniCalendar data={data} month={startOfMonth(today)} phases={phases} weekdays={weekdays} dateLocale={dateLocale} />
        </div>
      </div>

      <div className="mt-6">
        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#9B3926] mb-3">
          {t("fullCycleOverview")}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {groups.map((g) => {
            const p = phases[g.key];
            const s = g.days[0].date;
            const e = g.days[g.days.length - 1].date;
            return (
              <div key={p.key + s.toISOString()} className="rounded-xl border border-[#E5DFD3] p-3 bg-white">
                <div className="flex items-center gap-2">
                  <span className="h-6 w-1.5 rounded-full" style={{ backgroundColor: p.color }} />
                  <div>
                    <div className="font-serif text-lg leading-none" style={{ color: p.color }}>
                      {p.label}
                    </div>
                    <div className="text-[10px] text-[#8A857B]">
                      {format(s, "MMM d", { locale: dateLocale })}–{format(e, "MMM d", { locale: dateLocale })} · {t(p.focus === "fitness" ? "fitness" : "hormones")}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1.5 mt-2.5">
                  {metrics.map((m) => {
                    const Icon = METRIC_ICONS[m];
                    const dir = directionMeta(p[m].direction);
                    return (
                      <div key={m} className="flex flex-col items-center gap-1 border border-[#EEE8DC] rounded-lg py-1.5">
                        <div className="flex items-center gap-0.5">
                          <Icon className="h-3 w-3 text-[#5C5954]" />
                          <dir.Icon className="h-2.5 w-2.5" style={{ color: dir.color }} />
                        </div>
                        <Dots level={p[m].level} color={dir.color} />
                        <span className="text-[8px] font-semibold text-[#8A857B]">{t(METRIC_KEY_MAP[m])}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-[#E5DFD3] flex items-center justify-between text-[11px] text-[#8A857B]">
        <span>{t("exportFooter", { min: proteinBase, max: proteinMax })}</span>
        <span className="font-semibold">{t("generatedWith")}</span>
      </div>
    </div>
  );
});

export default ExportSheet;
